import React, { useMemo } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { MapPin, ExternalLink } from "lucide-react";

const GOOGLE_LIBRARIES = ["places"];
const CONTAINER_STYLE = { width: "100%", height: "320px", borderRadius: "16px" };

/**
 * Read-only Google Map on the public hostel detail page. Shows the owner-set
 * pin + a "Get directions" button that opens Google Maps in a new tab.
 *
 * Props: { lat, lng, address, name }
 * Silently renders nothing if the hostel has no map coordinates yet.
 */
export default function HostelMapView({ lat, lng, address, name }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-maps-loader",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: GOOGLE_LIBRARIES,
  });

  const numericLat = Number(lat);
  const numericLng = Number(lng);
  const hasCoords = Number.isFinite(numericLat) && Number.isFinite(numericLng) && (numericLat !== 0 || numericLng !== 0);
  const center = useMemo(() => ({ lat: numericLat, lng: numericLng }), [numericLat, numericLng]);

  if (!hasCoords) return null;

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${numericLat},${numericLng}`;

  return (
    <div data-testid="hostel-map-view" className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <MapPin size={18} className="text-vermilion-600" />
          <div className="font-display font-bold text-lg">Location</div>
        </div>
        <a
          href={directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hostel-map-directions-btn"
          className="inline-flex items-center gap-1 rounded-full bg-orange-800 hover:bg-orange-900 text-white h-9 px-4 text-xs font-semibold shadow-sm active:scale-95 transition-transform"
        >
          Get directions <ExternalLink size={12} />
        </a>
      </div>
      {loadError ? (
        <div className="rounded-xl bg-slate-50 border border-slate-200 text-slate-500 text-sm p-3">
          Map could not be loaded. Address: <b>{address || `${numericLat}, ${numericLng}`}</b>
        </div>
      ) : !isLoaded ? (
        <div className="h-[320px] rounded-2xl bg-slate-100 animate-pulse" aria-hidden />
      ) : (
        <>
          <GoogleMap
            mapContainerStyle={CONTAINER_STYLE}
            center={center}
            zoom={16}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              zoomControl: true,
              gestureHandling: "cooperative",
            }}
          >
            <Marker position={center} title={name || "Hostel"} />
          </GoogleMap>
          {address && (
            <div className="mt-3 text-sm text-slate-700 flex items-start gap-2">
              <MapPin size={14} className="mt-0.5 text-slate-500 flex-shrink-0" />
              <span>{address}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
